import { FlowStep, Question } from 'app-components'
import { CardNavigation } from '@/components'
import AddressSelector from '@/app/components/AddressSelector/AddressSelector'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { useMemo, useState } from 'react'
import { GuardableAction } from '@/app/components/FlowStep'
import { addHome } from '@/actions/homes'
import { getUtilities } from '@/actions/utilities'
import { getPlans } from '@/actions/plans'
import { getCurrentUser } from '@/actions/user'
import { GoogleLocationEvent } from '@/app/components/AddressSelector/useViewModel'
import { trackAddressSubmitted } from '@/logging/segment'

type Props = {
  onNext: () => void
}

function useViewModel(props: Props) {
  const dispatch = useAppDispatch()
  const [googlePlaceId, setGooglePlaceId] = useState<string | undefined | null>(
    undefined,
  )
  const homesAreLoading = useAppSelector((state) => state.homes.isLoading)
  const home = useAppSelector((state) => state.homes.selectedHome)
  const [isLoading, setIsLoading] = useState(false)

  const nextAction: GuardableAction = useMemo(
    () => ({
      action: () => {
        dispatch(addHome({ google_place_id: googlePlaceId })).then(async () => {
          // Track address submission for analytics
          trackAddressSubmitted()

          // Update caches for utilities and plans
          setIsLoading(true)
          await Promise.all([
            dispatch(getUtilities()),
            dispatch(getPlans()),
            dispatch(getCurrentUser()),
          ])
          setIsLoading(false)
          props.onNext()
        })
      },
      precondition: Boolean(googlePlaceId) && !homesAreLoading,
    }),
    [googlePlaceId, homesAreLoading],
  )

  const onGooglePlaceIdChange = (event: GoogleLocationEvent) => {
    setGooglePlaceId(event.place_id)
  }
  return { googlePlaceId, onGooglePlaceIdChange, nextAction, home, isLoading }
}

export default function AddAddressFlowStep(props: Props) {
  const { nextAction, onGooglePlaceIdChange, home, isLoading } =
    useViewModel(props)

  return (
    <FlowStep id="add-address-flow" onNext={nextAction}>
      <FlowStep.Content>
        <CardNavigation id="add-address-card-navigation" />
        <Question
          title={'What is your address?'}
          subtitle={
            'Provide your address so we can identify when you arrive at home. This also allows us to find your utility provider'
          }
        >
          <AddressSelector
            addresses={[]}
            initialAddress={home?.formatted_address}
            onAddressChange={onGooglePlaceIdChange}
            loading={isLoading}
          />
        </Question>
      </FlowStep.Content>
    </FlowStep>
  )
}
